import React from 'react';
import PropTypes from 'prop-types';
import { Panel, Table, Button, Label, Alert } from 'react-bootstrap';
import moment from 'moment';
import { withTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faUndo, faFileContract, faBan } from '@fortawesome/free-solid-svg-icons';
import { faEnvelope } from '@fortawesome/free-regular-svg-icons';
import { ConfirmationButton } from '../../common/components/formControls';
import ui from '../../common/services/ui';
import SubscriptionAcceptanceDialog from './SubscriptionAcceptanceDialog';
import SubscriptionEmailsDialog from './SubscriptionEmailsDialog';

const statusStyles = {
	Active: 'success',
	CancellationRequested: 'warning',
	Cancelled: 'danger',
	Ended: 'default'
};

// while the subscription is running, ending it goes through the Subscription panel instead of the payment's Cancel payment
export const isSubscriptionRunning = subscription => !!subscription && ['Active', 'CancellationRequested'].includes(subscription.status);

/**
 * The subscription contract behind an annual subscription payment: its dates, the cancellation request and the statutory notices.
 * Day-level dates come as yyyy-MM-dd in the centre's time zone.
 */
const SubscriptionPanel = ({ subscription, paymentId, canManage, canViewSentEmails, canEndNow, onRecordCancellation, onWithdrawCancellation, onEndNow, t }) => {
	const [showAcceptance, setShowAcceptance] = React.useState(false);
	const [showEmails, setShowEmails] = React.useState(false);
	const row = (label, value) => value ? <tr><th style={{ width: '40%' }}>{label}</th><td>{value}</td></tr> : null;
	// a requested cancellation processed straight away (nothing more to collect): lessons continue to the last day, but the provider is stopped
	const processedBeforeLastDay = subscription.status === 'Cancelled' && subscription.cancellationRequestedAt && subscription.lastDay &&
		subscription.lastDay >= moment().format('YYYY-MM-DD');

	return <Panel>
		<Panel.Heading>{t('PaymentHistory_SubscriptionContract')}</Panel.Heading>
		<Panel.Body>
			{processedBeforeLastDay && <Alert bsStyle='info'>{t('PaymentHistory_Subscription_CannotWithdrawNote')}</Alert>}
			<Table className='table-no-top-border'>
				<tfoot>
					{row(t('PaymentHistory_Subscription_Status'), <Label bsStyle={statusStyles[subscription.status]}>{t(`SubscriptionContractStatus_${subscription.status}`)}</Label>)}
					{row(t('PaymentHistory_Subscription_Period'), `${ui.formatDate(subscription.startDate)} - ${ui.formatDate(subscription.endDate)}`)}
					{row(t('PaymentHistory_Subscription_CoolingOffEnds'), subscription.coolingOffEndDate && ui.formatDate(subscription.coolingOffEndDate))}
					{row(t('PaymentHistory_Subscription_CancellationRequested'), subscription.cancellationRequestedAt && <span>
						{ui.formatDateTime(subscription.cancellationRequestedAt)}
						{subscription.cancellationRequestChannel && ` (${t(`CancellationRequestChannel_${subscription.cancellationRequestChannel}`)})`}
						{subscription.cancellationRequestedBy && <div className='text-muted'>{subscription.cancellationRequestedBy}</div>}
						{subscription.cancelledWithinCoolingOff && <div><Label bsStyle='info'>{t('PaymentHistory_Subscription_WithinCoolingOff')}</Label></div>}
					</span>)}
					{row(t('PaymentHistory_Subscription_RequestNotes'), subscription.cancellationRequestNotes)}
					{row(t('PaymentHistory_Subscription_LastDay'), subscription.lastDay && ui.formatDate(subscription.lastDay))}
					{row(t('PaymentHistory_Subscription_ProviderCancellationDue'), !subscription.cancellationFinalisedAt && subscription.providerCancellationDueAt && ui.formatDateTime(subscription.providerCancellationDueAt))}
					{row(t('PaymentHistory_Subscription_CancellationFinalised'), subscription.cancellationFinalisedAt && ui.formatDateTime(subscription.cancellationFinalisedAt))}
					{row(t('PaymentHistory_Subscription_CancellationNoticeSent'), subscription.cancellationConfirmationSentAt && ui.formatDateTime(subscription.cancellationConfirmationSentAt))}
					{row(t('PaymentHistory_Subscription_EndNoticeSent'), subscription.endNoticeSentAt && ui.formatDateTime(subscription.endNoticeSentAt))}
					{row(t('PaymentHistory_Subscription_NextReminder'), subscription.nextReminderDueAt && ui.formatDateTime(subscription.nextReminderDueAt))}
					{row(t('PaymentHistory_Subscription_LastReminder'), subscription.lastReminderChargeDate && ui.formatDate(subscription.lastReminderChargeDate))}
				</tfoot>
			</Table>
			{/* the evidence: what the parent accepted, and the statutory emails sent */}
			{subscription.hasAcceptance && <Button onClick={() => setShowAcceptance(true)} style={{ marginRight: '5px' }}><FontAwesomeIcon icon={faFileContract} /> {t('PaymentHistory_Subscription_ViewAcceptance')}</Button>}
			{subscription.hasAcceptance && <SubscriptionAcceptanceDialog show={showAcceptance} paymentId={paymentId} onCancel={() => setShowAcceptance(false)} />}
			{canViewSentEmails && <Button onClick={() => setShowEmails(true)} style={{ marginRight: '5px' }}><FontAwesomeIcon icon={faEnvelope} /> {t('PaymentHistory_Subscription_StatutoryEmails')}</Button>}
			{canViewSentEmails && <SubscriptionEmailsDialog show={showEmails} subscriptionContractId={subscription.id} onCancel={() => setShowEmails(false)} />}
			{canManage && subscription.canRecordCancellation &&
				<Button onClick={onRecordCancellation} bsStyle='danger' style={{ marginRight: '5px' }}><FontAwesomeIcon icon={faTimes} /> {t('PaymentHistory_Subscription_RecordCancellation')}</Button>}
			{canManage && subscription.canWithdrawCancellation && <ConfirmationButton
				onConfirmation={onWithdrawCancellation}
				confirmationTitle={t('PaymentHistory_Subscription_WithdrawCancellation')}
				confirmationMessage={t('PaymentHistory_Subscription_WithdrawCancellationConfirmation')}
				displayType={'inline'}
				bsStyle='warning'
				icon={faUndo}
				label={t('PaymentHistory_Subscription_WithdrawCancellation')} />}
			{/* the admin ending it regardless of the notice terms - Cancel payment under another name */}
			{canEndNow && isSubscriptionRunning(subscription) &&
				<Button onClick={onEndNow} style={{ marginLeft: subscription.canWithdrawCancellation ? '5px' : 0 }}><FontAwesomeIcon icon={faBan} /> {t('PaymentHistory_Subscription_EndNow')}</Button>}
		</Panel.Body>
	</Panel>;
};

SubscriptionPanel.propTypes = {
	subscription: PropTypes.object.isRequired,
	paymentId: PropTypes.number,
	canManage: PropTypes.bool,
	canViewSentEmails: PropTypes.bool,
	canEndNow: PropTypes.bool,
	onRecordCancellation: PropTypes.func,
	onWithdrawCancellation: PropTypes.func,
	onEndNow: PropTypes.func
};

export default withTranslation()(SubscriptionPanel);
